import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Accordion, AccordionContent, AccordionItem, AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from "sonner";
import TranslateWidget from "@/components/TranslateWidget";
import { useSiteContentCache, SiteLoadingScreen } from "@/contexts/SiteContentCache";
import { Helmet } from "react-helmet-async";
import { 
  Menu, X, Users, Trophy, Heart, Mail, Phone, MapPin,
  Instagram, Facebook, Twitter, CheckCircle2, ArrowLeft
} from "lucide-react";

const JOIN_BG = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/b5edc9127c51a442369721d6cda077d9d79d6ba35c55b3b335685126bd4a8e18.png";
const YOUTH_MATCH = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/b2f0b4c9b1e72b4699ba0b7950d28c1d9bf8edf25108062f47dafd67c47ef853.png";
const YOUTH_TRAINING = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/01d0a7f9d0fae841063bad83f32292add55537d02cff1c66c93c759fd09f58a5.png";
const YOUTH_CELEBRATE = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/1208aa45e84dfc188966cce37da4ea7a714d6688e1291607a69ee8372be9b17b.png";

const FEATURE_ICONS = [Users, Trophy, Heart];

const JOIN_DEFAULTS = {
  hero: {
    badge: "Join the club where our players make the difference!",
    title: "COMMONWEALTH FC TRYOUTS",
    description: "Experience top-tier training and development with Commonwealth FC. Sign up for our upcoming tryouts and become part of a community dedicated to excellence in youth soccer.",
    button_text: "Register for Information",
  },
  features: [
    { title: "LICENSED, EXPERIENCED COACHES", description: "Our coaching staff holds the highest certifications and brings years of player development experience." },
    { title: "SUCCESS DEVELOPING ALL AGES", description: "We have a proven track record of developing players from U8 through high school and beyond." },
    { title: "COMPETITIVE TOURNAMENTS", description: "Compete in top-tier tournaments and leagues to test your skills against the best." },
  ],
  form: {
    heading: "REGISTER FOR TRYOUTS AND EVALUATIONS",
    description: "Sign up now to get information on how to secure your spot at the Commonwealth FC tryouts. Please fill out the form to get started.",
    form_title: "GET STARTED",
    success_title: "You're In!",
    success_message: "Thank you for your interest in Commonwealth FC. We'll be in touch within 48 hours to schedule your evaluation.",
  },
  age_groups: [
    { age: "U10-U11", birth: "2017-2015", status: "Limited Evaluations" },
    { age: "U12-U14", birth: "2014-2012", status: "Open" },
    { age: "U16", birth: "2011-2010", status: "Open" },
    { age: "U18", birth: "2009-2008", status: "Limited Evaluations" },
    { age: "Girls High School", birth: "2007-2011", status: "Actively Seeking Players" },
  ],
  faqs: [
    { question: "What should my child wear to tryouts?", answer: "Your child should wear comfortable athletic clothing, soccer cleats, and shin guards. Don't forget to bring a water bottle for hydration." },
    { question: "How do I register for tryouts?", answer: "Registration can be completed through our website. Simply fill out the interest form above and we'll reach out with scheduling information." },
    { question: "What is the cost of tryouts?", answer: "There is no cost for coming to a tryout or evaluation session. We want to make it easy for everyone to experience our program." },
    { question: "When will we know the results?", answer: "Results will be communicated via email within 48 hours after the tryouts. Please ensure your contact details are up-to-date." },
    { question: "Can parents watch the tryouts?", answer: "Yes, parents are welcome to observe from designated areas. We encourage support while maintaining a respectful distance from the field." },
  ],
  cta: {
    title: "READY TO KICK OFF YOUR SOCCER JOURNEY?",
    subtitle: "With a club that cares about your player",
    badge: "Secure your spot for Commonwealth FC tryouts",
    button_text: "Register Now",
  },
  age_options: [
    "U8 (2018-2019)", "U10 (2016-2017)", "U12 (2014-2015)",
    "U14 (2012-2013)", "U16 (2010-2011)", "U18 (2008-2009)", "High School Girls",
  ],
  form_fields: [
    { id: "name", label: "Player Name", type: "text", placeholder: "Player's full name", required: true, enabled: true, maps_to: "name", options: [] },
    { id: "parent_name", label: "Parent / Guardian Name", type: "text", placeholder: "Parent or guardian's full name", required: true, enabled: true, maps_to: "parent_name", options: [] },
    { id: "email", label: "Email Address", type: "email", placeholder: "parent@email.com", required: true, enabled: true, maps_to: "email", options: [] },
    { id: "phone", label: "Phone #", type: "tel", placeholder: "(555) 555-5555", required: true, enabled: true, maps_to: "phone", options: [] },
    { id: "player_age", label: "What age group are you interested in?", type: "select", placeholder: "Select age group", required: true, enabled: true, maps_to: "player_age", options: ["U8 (2018-2019)", "U10 (2016-2017)", "U12 (2014-2015)", "U14 (2012-2013)", "U16 (2010-2011)", "U18 (2008-2009)", "High School Girls"] },
    { id: "soccer_experience", label: "Additional Information / Soccer Experience (Optional)", type: "textarea", placeholder: "Previous clubs, years playing, positions...", required: false, enabled: true, maps_to: "soccer_experience", options: [] },
  ],
};

const JoinPage = () => {
  const { data: cachedContent, loading: cacheLoading } = useSiteContentCache();

  if (!cachedContent && cacheLoading) return <SiteLoadingScreen />;

  const siteContent = cachedContent || {};

  const joinContent = (() => {
    const jp = siteContent.join_page || {};
    return {
      hero: { ...JOIN_DEFAULTS.hero, ...jp.hero },
      features: jp.features?.length > 0 ? jp.features : JOIN_DEFAULTS.features,
      form: { ...JOIN_DEFAULTS.form, ...jp.form },
      age_groups: jp.age_groups?.length > 0 ? jp.age_groups : JOIN_DEFAULTS.age_groups,
      faqs: jp.faqs?.length > 0 ? jp.faqs : JOIN_DEFAULTS.faqs,
      cta: { ...JOIN_DEFAULTS.cta, ...jp.cta },
      age_options: jp.age_options?.length > 0 ? jp.age_options : JOIN_DEFAULTS.age_options,
      form_fields: jp.form_fields?.length > 0 ? jp.form_fields : JOIN_DEFAULTS.form_fields,
      images: {
        bg_url: jp.images?.bg_url || JOIN_BG,
        slider1_url: jp.images?.slider1_url || YOUTH_MATCH,
        slider2_url: jp.images?.slider2_url || YOUTH_TRAINING,
        slider3_url: jp.images?.slider3_url || YOUTH_CELEBRATE,
      },
    };
  })();

  const logoUrl = siteContent.logo_url || "";
  const ft = siteContent.footer || {};

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Join Commonwealth FC | Youth Soccer Registration Charlottesville VA</title>
        <meta name="description" content="Register for Commonwealth FC youth soccer tryouts and training sessions in Charlottesville, VA. Affordable registration, all age groups U8-U19. Apply for a free evaluation today." />
        <meta property="og:title" content="Join Commonwealth FC | Youth Soccer Registration" />
        <meta property="og:description" content="Register for youth soccer tryouts in Charlottesville. Affordable, competitive teams for all ages." />
        <meta property="og:url" content="https://commonwealthfc.org/join" />
        <link rel="canonical" href="https://commonwealthfc.org/join" />
      </Helmet>
      <Navigation logoUrl={logoUrl} />
      <HeroSection content={joinContent.hero} />
      <ImageCarousel images={joinContent.images} />
      <FeaturesSection features={joinContent.features} />
      <JoinFormSection content={joinContent.form} formFields={joinContent.form_fields} ageOptions={joinContent.age_options} socialLinks={ft} consentText={siteContent.join_page?.consent_text} bgImage={joinContent.images.bg_url} />
      <AgeGroupsSection ageGroups={joinContent.age_groups} />
      <FAQSection faqs={joinContent.faqs} />
      <CTASection content={joinContent.cta} />
      <JoinFooter logoUrl={logoUrl} ft={ft} />
    </div>
  );
};

const Navigation = ({ logoUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#0C2E3C] shadow-lg" : "bg-[#0C2E3C]/95"}`}
      data-testid="join-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={logoUrl} alt="Commonwealth FC" className="h-9 w-auto object-contain" />
            <span className="text-sm font-bold uppercase tracking-wider text-[#F0F4F3] hidden sm:block">Commonwealth FC</span>
          </Link>
          <div className="hidden md:flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-[#F0F4F3]/90 hover:text-[#C8E64E] font-semibold text-sm uppercase tracking-wider transition-colors" data-testid="back-to-main-link">
              <ArrowLeft size={16} /> Back
            </Link>
            <TranslateWidget />
          </div>
          <button className="md:hidden text-[#F0F4F3]" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
        {isOpen && (
          <div className="md:hidden bg-[#0C2E3C] border-t border-[#F0F4F3]/20 py-4">
            <Link to="/" className="flex items-center gap-2 py-3 px-4 text-[#F0F4F3]/90 hover:text-[#C8E64E] font-medium tracking-wider" onClick={() => setIsOpen(false)}>
              <ArrowLeft size={16} /> BACK TO MAIN SITE
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = ({ content }) => (
  <section className="relative pt-32 pb-16 bg-[#0C2E3C]" data-testid="join-hero-section">
    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-[#C8E64E] font-semibold text-sm uppercase tracking-widest mb-4">{content.badge}</p>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F0F4F3] mb-6">{content.title}</h1>
      <p className="text-[#F0F4F3]/80 text-lg max-w-2xl mx-auto mb-8">{content.description}</p>
      <Button
        onClick={() => document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth" })}
        className="bg-[#C8E64E] hover:bg-[#B5D33E] text-[#0C2E3C] font-bold text-sm uppercase tracking-wider px-8 h-14 rounded-none"
        data-testid="hero-register-btn"
      >
        {content.button_text}
      </Button>
    </div>
  </section>
);

const ImageCarousel = ({ images }) => (
  <section className="py-16 bg-[#F0F4F3]" data-testid="image-carousel">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-4">
        {[images.slider1_url, images.slider2_url, images.slider3_url].map((img, index) => (
          <div key={`carousel-${index}`} className="relative aspect-[4/5] overflow-hidden group cursor-pointer" onClick={() => document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth" })}>
            <img src={img} alt={`Youth soccer ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-[#0C2E3C]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-[#F0F4F3] font-bold tracking-wider">CLICK HERE</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FeaturesSection = ({ features }) => (
  <section className="py-20 bg-[#F0F4F3]" data-testid="join-features-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-8">
        {features.map((feature, idx) => {
          const Icon = FEATURE_ICONS[idx % FEATURE_ICONS.length];
          return (
            <div key={`feature-${feature.title}`} className="text-center p-8 border border-[#0C2E3C]/20 hover:border-[#0C2E3C] transition-colors" data-testid={`join-feature-card-${idx}`}>
              <Icon className="w-12 h-12 text-[#0C2E3C] mx-auto mb-6" strokeWidth={1} />
              <h3 className="font-bold text-base uppercase tracking-wider text-[#0C2E3C] mb-4">{feature.title}</h3>
              <p className="text-[#6B8A8F] text-base leading-relaxed">{feature.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const DEFAULT_CONSENT = "By submitting this form, you agree to receive text messages from Commonwealth FC regarding registration, scheduling, and program updates. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.";

const KNOWN_LEAD_FIELDS = ["name", "email", "phone", "player_age", "parent_name", "soccer_experience", "organization", "num_players", "message"];

const JoinFormSection = ({ content, formFields, ageOptions, socialLinks, consentText, bgImage }) => {
  const activeFields = (formFields || []).filter(f => f.enabled);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formLoadedAt] = useState(() => Date.now() / 1000);

  const setField = (id, value) => setFormData(prev => ({ ...prev, [id]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { lead_type: "player", form_loaded_at: formLoadedAt };
      const custom = {};
      activeFields.forEach(field => {
        const val = formData[field.id] || "";
        if (field.maps_to && KNOWN_LEAD_FIELDS.includes(field.maps_to)) {
          payload[field.maps_to] = val;
        } else if (val) {
          custom[field.label] = val;
        }
      });
      if (Object.keys(custom).length > 0) payload.custom_fields = custom;
      // Anti-spam: include honeypot value (should be empty for real users)
      if (formData.website) payload.website = formData.website;
      // Ensure required backend fields have fallbacks
      if (!payload.name) payload.name = payload.email || "Unknown";
      if (!payload.email) payload.email = "noemail@placeholder.com";
      if (!payload.phone) payload.phone = "";
      if (!payload.player_age) payload.player_age = "Not specified";
      await axios.post(`${API}/leads/submit`, payload);
      setSubmitted(true);
      toast.success("Thank you for your interest! We'll be in touch soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = loading || activeFields.some(f => f.required && !formData[f.id]?.trim?.());

  return (
    <section id="interest-form" className="py-24 relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }} data-testid="join-form-section">
      <div className="absolute inset-0 bg-[#0C2E3C]/30" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#F0F4F3] mb-6">{content.heading}</h2>
            <p className="text-[#F0F4F3]/80 text-lg mb-8">{content.description}</p>
            <div className="flex gap-4">
              {socialLinks.facebook && <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Facebook size={24} /></a>}
              {socialLinks.instagram && <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Instagram size={24} /></a>}
              {!socialLinks.facebook && !socialLinks.instagram && (
                <>
                  <span className="text-[#F0F4F3]/40"><Facebook size={24} /></span>
                  <span className="text-[#F0F4F3]/40"><Instagram size={24} /></span>
                </>
              )}
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-sm p-8 border-t-4 border-[#C8E64E]">
            {submitted ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-[#0C2E3C] mb-2">{content.success_title}</h3>
                <p className="text-[#6B8A8F]">{content.success_message}</p>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-2xl font-bold text-[#0C2E3C] mb-6">{content.form_title}</h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {activeFields.map((field) => (
                    <div key={field.id}>
                      <Label htmlFor={field.id} className="text-[#0C2E3C] font-semibold text-sm">{field.label}</Label>
                      {field.type === "select" ? (
                        <select
                          id={field.id}
                          required={field.required}
                          value={formData[field.id] || ""}
                          onChange={(e) => setField(field.id, e.target.value)}
                          className="mt-1 w-full h-10 px-3 border border-[#E5E0D8] bg-white text-[#0C2E3C] focus:border-[#0C2E3C] focus:outline-none focus:ring-1 focus:ring-[#0C2E3C]"
                          data-testid={`join-field-${field.id}`}
                        >
                          <option value="">{field.placeholder || "Select..."}</option>
                          {(field.options || ageOptions || []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : field.type === "textarea" ? (
                        <textarea
                          id={field.id}
                          required={field.required}
                          value={formData[field.id] || ""}
                          onChange={(e) => setField(field.id, e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-[#E5E0D8] bg-white text-[#0C2E3C] focus:border-[#0C2E3C] focus:outline-none focus:ring-1 focus:ring-[#0C2E3C] min-h-[80px] resize-y text-sm"
                          placeholder={field.placeholder}
                          rows={3}
                          data-testid={`join-field-${field.id}`}
                        />
                      ) : (
                        <Input
                          id={field.id}
                          type={field.type}
                          required={field.required}
                          value={formData[field.id] || ""}
                          onChange={(e) => setField(field.id, e.target.value)}
                          className="mt-1 border-[#E5E0D8] rounded-none focus:border-[#0C2E3C] focus:ring-[#0C2E3C]"
                          placeholder={field.placeholder}
                          data-testid={`join-field-${field.id}`}
                        />
                      )}
                    </div>
                  ))}
                  {/* Honeypot — hidden from real users, bots auto-fill it */}
                  <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true" tabIndex={-1}>
                    <input
                      type="text"
                      name="website"
                      value={formData.website || ""}
                      onChange={(e) => setField("website", e.target.value)}
                      autoComplete="off"
                      tabIndex={-1}
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitDisabled} className="w-full bg-[#0C2E3C] hover:bg-[#0A2430] text-[#F0F4F3] font-bold uppercase tracking-wider text-base h-14 rounded-none" data-testid="join-submit-btn">
                    {loading ? "SUBMITTING..." : "SUBMIT"}
                  </Button>
                  {(consentText !== "" && (consentText || DEFAULT_CONSENT)) && (
                    <p className="text-[11px] text-[#6B8A8F] leading-relaxed mt-3" data-testid="join-consent-text">
                      {consentText || DEFAULT_CONSENT}
                    </p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const AgeGroupsSection = ({ ageGroups }) => (
  <section className="py-24 bg-[#F0F4F3]" data-testid="age-groups-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-heading text-4xl font-bold text-[#0C2E3C] mb-4 text-center">AGE GROUPS & SCHEDULE</h2>
      <p className="text-center text-[#6B8A8F] mb-12 max-w-2xl mx-auto">Find your team and join our next evaluation session</p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#0C2E3C] text-[#F0F4F3]">
              <th className="font-semibold uppercase tracking-wider text-left p-4 text-sm">Age Group</th>
              <th className="font-semibold uppercase tracking-wider text-left p-4 text-sm">Birth Year</th>
              <th className="font-semibold uppercase tracking-wider text-left p-4 text-sm">Open Tryouts?</th>
            </tr>
          </thead>
          <tbody>
            {ageGroups.map((group, idx) => (
              <tr key={`ag-${group.age}`} className={`${idx % 2 === 0 ? "bg-white" : "bg-[#F0F4F3]"} border-b border-[#E5E0D8]`} data-testid={`age-group-row-${group.age}`}>
                <td className="p-4 font-bold text-[#0C2E3C]">{group.age}</td>
                <td className="p-4 text-[#6B8A8F]">{group.birth}</td>
                <td className={`p-4 font-medium ${group.status === "Open" ? "text-green-600" : group.status.includes("Actively") ? "text-[#C8E64E]" : "text-[#6B8A8F]"}`}>
                  {group.status}
                  {group.status.includes("Actively") && <span className="block text-xs mt-1">*Actively Seeking Players*</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

const FAQSection = ({ faqs }) => (
  <section className="py-24 bg-white" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="font-heading text-4xl font-bold text-[#0C2E3C] mb-4 text-center">COMMON QUESTIONS ABOUT TRYOUTS</h2>
      <p className="text-center text-[#6B8A8F] mb-12">Find answers to the most frequently asked questions about our tryout process</p>
      <Accordion type="single" collapsible className="space-y-4">
        {faqs.map((faq, index) => (
          <AccordionItem key={`faq-${faq.question.slice(0, 20)}`} value={`item-${index}`} className="border border-[#E5E0D8] px-6" data-testid={`faq-item-${index}`}>
            <AccordionTrigger className="font-semibold text-base text-[#0C2E3C] hover:text-[#C8E64E]">{faq.question}</AccordionTrigger>
            <AccordionContent className="text-[#6B8A8F] text-base leading-relaxed">{faq.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

const CTASection = ({ content }) => (
  <section className="py-20 bg-[#0C2E3C]" data-testid="join-cta-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#F0F4F3] mb-4">{content.title}</h2>
      <p className="text-[#F0F4F3]/70 mb-8">{content.subtitle}</p>
      <h3 className="font-semibold text-sm uppercase tracking-wider text-[#C8E64E] mb-8">{content.badge}</h3>
      <Button onClick={() => document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth" })} className="bg-[#C8E64E] hover:bg-[#B5D33E] text-[#0C2E3C] font-bold uppercase tracking-wider text-sm px-8 h-14 rounded-none">
        {content.button_text}
      </Button>
    </div>
  </section>
);

const JoinFooter = ({ logoUrl, ft }) => (
  <footer className="bg-[#0C2E3C] py-8 border-t border-[#F0F4F3]/10" data-testid="join-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <img src={logoUrl} alt="Commonwealth FC" className="h-10 w-auto object-contain" />
            <div>
              <h3 className="font-bold text-lg text-[#F0F4F3]">Commonwealth FC</h3>
              <p className="text-[#F0F4F3]/50 text-sm mt-0.5">BUILT DIFFERENT. BUILT TOGETHER.</p>
            </div>
          </div>
          <p className="text-[#F0F4F3]/60 text-base mb-4 max-w-sm">
            Developing talented youth soccer players through quality coaching and a family-first approach.
          </p>
          <div className="flex gap-4">
            {ft.instagram && <a href={ft.instagram} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Instagram"><Instagram size={20} /></a>}
            {ft.twitter && <a href={ft.twitter} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Twitter"><Twitter size={20} /></a>}
            {ft.facebook && <a href={ft.facebook} target="_blank" rel="noopener noreferrer" className="text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors" aria-label="Facebook"><Facebook size={20} /></a>}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Contact</h4>
          <div className="space-y-3 text-sm">
            {ft.email && <a href={`mailto:${ft.email}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Mail size={16} />{ft.email}</a>}
            {ft.phone && <a href={`tel:${ft.phone}`} className="flex items-center gap-2 text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors"><Phone size={16} />{ft.phone}</a>}
            {ft.location && <p className="flex items-center gap-2 text-[#F0F4F3]/60"><MapPin size={16} />{ft.location}</p>}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-sm uppercase tracking-wider text-[#F0F4F3] mb-4">Quick Links</h4>
          <div className="space-y-2 text-sm">
            <Link to="/" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Home</Link>
            <Link to="/join" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Register</Link>
            <Link to="/coaches" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Bring Your Team</Link>
            <Link to="/livestream" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Live Streams</Link>
            <Link to="/admin/login" className="block text-[#F0F4F3]/60 hover:text-[#C8E64E] transition-colors">Admin Login</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-[#F0F4F3]/10 mt-6 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[#F0F4F3]/40 text-sm">
        <span>&copy; {new Date().getFullYear()} Commonwealth FC. All rights reserved.</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-[#C8E64E] transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#C8E64E] transition-colors">Terms</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default JoinPage;
